import { Link, type LinkProps } from '@tanstack/react-router';
import type { ReactNode } from 'react';

export type ButtonProps = {
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	mode?: 'primary' | 'secondary' | 'tertiary' | 'outline' | 'inverse' | 'transparent' | 'danger' | 'custom';
	custom?: string;
	to?: LinkProps['to'];
	params?: LinkProps['params'];
	type?: 'button' | 'reset' | 'submit';
	className?: string;
	children: ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
	React.AnchorHTMLAttributes<HTMLAnchorElement>;

export function Button({
	to,
	size = 'md',
	mode = 'primary',
	className = '',
	type = 'button',
	children,
	custom = 'gray',
	...props
}: ButtonProps) {
	const sizes: Record<typeof size, string> = {
		xs: 'gap-1 rounded-md p-1 text-xs leading-5',
		sm: 'gap-1 rounded-md p-1.5 text-sm leading-5',
		md: 'gap-2 rounded-lg px-4 py-3 text-base leading-5',
		lg: 'gap-3 rounded-xl px-5 py-4 text-lg leading-6',
		xl: 'gap-4 rounded-xl px-6 py-5 text-xl leading-6',
	};

	const modes: Record<typeof mode, string> = {
		primary:
			'bg-gradient-to-br from-[var(--theme-400)] to-[var(--theme-500)] text-white hover:from-[var(--theme-300)] hover:to-[var(--theme-400)] active:from-[var(--theme-500)] active:to-[var(--theme-600)]',
		secondary: 'bg-gray-100 text-black hover:bg-gray-200 active:bg-gray-300',
		tertiary: 'bg-black text-white',
		outline: 'border border-gray-400 text-black hover:bg-gray-100',
		inverse: 'bg-white text-black hover:bg-gray-100',
		transparent: 'bg-none text-black hover:bg-gray-100',
		danger: 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700',
		custom: `border-${custom}-700 text-${custom}-900 bg-${custom}-200 dark:border-${custom}-700 dark:text-${custom}-50 dark:bg-${custom}-500`,
	};

	const classes = `
		${sizes[size]}
		${modes[mode]}
		${className}
		flex w-fit font-medium items-center justify-center
		focus:shadow-[_0px_0px_0px_2px_#ffffff,0px_0px_0px_5px_var(--theme-transparent)]
		disabled:opacity-40
	`;

	return to ? (
		<Link {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)} to={to} className={classes}>
			{children}
		</Link>
	) : (
		<button {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)} type={type} className={classes}>
			{children}
		</button>
	);
}
